import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Briefcase, Network, ArrowUpRight, Award } from 'lucide-react';

// --- DATA ---
const defenseMentors = [
    { name: "Lt Gen Surinder Singh Mahal", role: "Strategic Operations", link: "https://www.linkedin.com/in/lt-gen-surinder-singh-mahal-7789b82b9" },
    { name: "Col Virbhadra Singh Rawat", role: "Defense Strategy", link: "https://www.linkedin.com/in/virbhadra-singh-rawat-018359168" },
    { name: "Maj Gen Ashish Jindal", role: "Defense Technology", link: "https://www.linkedin.com/in/major-general-ashish-jindal-retd-96474135" },
    { name: "Maj Gen Mandeep Singh", role: "Security & Tech", link: "https://www.linkedin.com/in/major-general-dr-mandip-singh-sm-vsm-retd-6250161a7" },
    { name: "Cmde Mukesh Bhargava", role: "Naval & Aerospace", link: "https://www.linkedin.com/in/commodore-mukesh-bhargava-9709b610" },
    { name: "Lt Col Prashant Malik", role: "Tactical Innovation", link: "https://www.linkedin.com/in/prashant-malik1977" },
    { name: "Col Prabir Sengupta", role: "Leadership & Scaling", link: "https://www.linkedin.com/in/colprabirsengupta" }
];

const industryMentors = [
    { name: "Sanjeev Bikhchandani", role: "Entrepreneurship", link: "https://www.linkedin.com/in/sanjeev-bikhchandani-501261" },
    { name: "Arindam Mukhopadhyay", role: "Business Strategy", link: "https://x.com/arin9856" },
    { name: "Deb Mukherji", role: "Investment & Growth", link: "https://www.linkedin.com/in/deb-mukherji-ph-d-a0b4bb12" },
    { name: "Vikrant Varshney", role: "Corporate Innovation", link: "https://www.linkedin.com/in/vikrantvarshney-indovation" }
];

// --- ANIMATION VARIANTS ---
const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const fadeScale = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

export default function Mentors() {
    return (
        <section className="relative bg-[#f8fafc] py-24 px-4 sm:px-6 lg:px-8 overflow-hidden selection:bg-blue-600 selection:text-white">

            {/* Minimalist Background Gradients */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-blue-100/50 to-transparent blur-3xl pointer-events-none"></div>

            <div className="max-w-7xl mx-auto relative z-10">

                {/* Header Section */}
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mb-6"
                    >
                        <Award size={16} className="text-blue-600" />
                        <span className="text-slate-800 font-semibold uppercase text-xs tracking-widest">
                            Ecosystem Leadership
                        </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mb-6"
                    >
                        Guided by the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Best Minds.</span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-lg md:text-xl text-slate-600 font-light leading-relaxed"
                    >
                        Our startups are backed by decades of experience from decorated military leaders, industry stalwarts, and academic pioneers.
                    </motion.p>
                </div>

                {/* Bento Box Grid */}
                <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-6"
                >
                    {/* 1. Defense & Aerospace (Spans 7 columns) */}
                    <motion.div variants={fadeScale} className="lg:col-span-7 bg-white rounded-[2rem] p-8 md:p-10 border border-slate-200/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden group">
                        {/* Hover Gradient Aura */}
                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-colors duration-500"></div>

                        <div className="flex items-center gap-4 mb-8 relative z-10">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center text-slate-800 shadow-sm">
                                <Shield size={26} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-slate-900">Defense & Aerospace</h3>
                                <p className="text-slate-500 text-sm font-medium">Strategic National Security Experts</p>
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 relative z-10">
                            {defenseMentors.map((mentor, index) => (
                                <MentorCard key={index} mentor={mentor} />
                            ))}
                        </div>
                    </motion.div>

                    {/* 2. Industry Stalwarts (Spans 5 columns) */}
                    <motion.div variants={fadeScale} className="lg:col-span-5 bg-white rounded-[2rem] p-8 md:p-10 border border-slate-200/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden group flex flex-col">
                        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-cyan-500/10 rounded-full blur-3xl group-hover:bg-cyan-500/20 transition-colors duration-500"></div>

                        <div className="flex items-center gap-4 mb-8 relative z-10">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center text-slate-800 shadow-sm">
                                <Briefcase size={26} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-slate-900">Industry Stalwarts</h3>
                                <p className="text-slate-500 text-sm font-medium">Corporate & Startup Leaders</p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3 relative z-10 flex-grow">
                            {industryMentors.map((mentor, index) => (
                                <MentorCard key={index} mentor={mentor} />
                            ))}
                        </div>
                    </motion.div>

                    {/* 3. Deep Academic Integration (White Flex with Blue Gradient Border) */}
                    <motion.div
                        variants={fadeScale}
                        className="lg:col-span-12 mt-2 group shadow-xl hover:shadow-2xl transition-shadow duration-300 rounded-[2rem] p-[2px] bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400"
                    >
                        {/* Inner White Container */}
                        <div className="bg-white rounded-[calc(2rem-2px)] p-8 md:p-12 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-10 h-full w-full">

                            {/* Subtle background elements */}
                            <div className="absolute inset-0 opacity-[0.4]" style={{ backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[100px] pointer-events-none group-hover:bg-blue-500/10 transition-colors duration-500"></div>

                            <div className="relative z-10 flex-1">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-blue-600">
                                        <Network size={24} />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
                                        Deep Academic Integration
                                    </h3>
                                </div>
                                <p className="text-slate-600 text-lg max-w-2xl font-light leading-relaxed">
                                    Beyond external advisors, our startups gain exclusive access to a massive internal network for technical consulting, research validation, and deep-tech prototyping.
                                </p>
                            </div>

                            <div className="relative z-10 shrink-0 text-center md:text-right">
                                <div className="text-6xl md:text-7xl font-black text-slate-900 tracking-tighter mb-2">
                                    180<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">+</span>
                                </div>
                                <div className="text-slate-500 font-bold uppercase tracking-[0.2em] text-sm">
                                    Faculty Domain Experts
                                </div>
                            </div>
                        </div>
                    </motion.div>

                </motion.div>
            </div>
        </section>
    );
}

// --- SUB-COMPONENT: Sleek Directory Item ---
const MentorCard = ({ mentor }) => (
    <a
        href={mentor.link}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center justify-between p-4 rounded-2xl border border-transparent hover:bg-slate-50 hover:border-slate-200 transition-all duration-300"
    >
        <div className="flex flex-col overflow-hidden pr-4">
            <span className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors truncate">
                {mentor.name}
            </span>
            <span className="text-xs text-slate-500 font-medium uppercase tracking-wider mt-1 truncate">
                {mentor.role}
            </span>
        </div>

        {/* Animated Arrow Button */}
        <div className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0 group-hover:bg-blue-600 group-hover:border-blue-600 transition-all duration-300 overflow-hidden relative">
            {/* Default arrow */}
            <ArrowUpRight
                size={18}
                className="text-slate-400 absolute group-hover:-translate-y-6 group-hover:translate-x-6 transition-transform duration-300"
            />
            {/* Arrow that slides in on hover */}
            <ArrowUpRight
                size={18}
                className="text-white absolute translate-y-6 -translate-x-6 group-hover:translate-y-0 group-hover:translate-x-0 transition-transform duration-300"
            />
        </div>
    </a>
);